import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchArticlesBySubCategory } from "../api/consumerArticlesApi";
import {
  ensureUniqueArticleSlugs,
  normalizeConsumerArticle,
} from "../utils/consumerArticleHelpers";
import { resolveTopicBackendIds } from "../utils/resolveTopicBackendIds";
import {
  filterRowsForTopic,
  isBenignEmptyContentMessage,
} from "../utils/topicContentMatch";

export function useTopicArticles({
  signupCategories,
  industryId,
  menuItem,
  page = 1,
  limit = 24,
  enabled = true,
}) {
  const topicIds = useMemo(
    () => resolveTopicBackendIds(signupCategories, industryId, menuItem),
    [signupCategories, industryId, menuItem],
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!enabled || !topicIds?.categoryName || !topicIds?.subCategoryName) {
      setItems([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await fetchArticlesBySubCategory({
      category: topicIds.categoryName,
      subCategory: topicIds.subCategoryName,
      page,
      limit,
    });

    if (!result.status) {
      const message = result.message || "Unable to load articles.";
      setItems([]);
      setError(isBenignEmptyContentMessage(message) ? null : message);
    } else {
      const scoped = filterRowsForTopic(result.data, topicIds);
      setItems(ensureUniqueArticleSlugs(scoped).map(normalizeConsumerArticle));
      setError(null);
    }
    setLoading(false);
  }, [enabled, topicIds, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    topicIds,
    articles: items,
    totalCount: items.length,
    loading,
    error,
    reload: load,
  };
}
